// src/components/AuthModal.js — extracted from App.js
import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { SUPABASE_FUNCTIONS_URL, SUPABASE_ANON_KEY } from "../constants";
import { capabilitiesHtml } from "../lib/utils";
import { Logo } from "./common";

// ─── Auth Modal ───────────────────────────────────────────────────────────────
export function AuthModal({ onLogin, onClose, onUserCreated }) {
  const [mode, setMode]       = useState("login"); // login | signup | forgot
  const [step, setStep]       = useState("details"); // details | otp | new_password
  const [email, setEmail]     = useState("");
  const [pass, setPass]       = useState("");
  const [name, setName]       = useState("");
  const [phone, setPhone]     = useState("");
  const [otp, setOtp]         = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr]         = useState("");
  const [resetDone, setResetDone] = useState(false);

  // Restore pending signup if user left mid-OTP and came back
  useEffect(() => {
    try {
      const saved = localStorage.getItem("thc-pending-signup");
      if (saved) {
        const { email: e, name: n, phone: ph } = JSON.parse(saved);
        setMode("signup"); setStep("details");
        setEmail(e || ""); setName(n || ""); setPhone(ph || "");
      }
    } catch {}
  }, []);

  const handleLogin = async () => {
    if (!email||!pass) { setErr("Fill all required fields."); return; }
    setSending(true); setErr("");
    try {
      const { data, error } = await supabase.functions.invoke("auth-login", { body: { action:"login", email: email.trim(), password: pass } });
      if (error || data?.error) throw new Error(data?.error || "Login failed");
      // Establish Supabase Auth JWT session (non-blocking — custom auth still works if this fails)
      supabase.auth.signInWithPassword({ email: email.trim(), password: pass }).catch(() => {});
      onLogin(data.user);
    } catch(e) { setErr(e.message); }
    finally { setSending(false); }
  };

  const handleSendOtp = async () => {
    if (!name||!email||!pass) { setErr("Fill all required fields."); return; }
    setSending(true); setErr("");
    try {
      // Check email uniqueness server-side
      const { data: chk } = await supabase.functions.invoke("auth-login", { body: { action:"check_email", email: email.trim() } });
      if (chk?.exists) { setErr("Email already registered."); setSending(false); return; }
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/send-otp`, {
        method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${SUPABASE_ANON_KEY}`},
        body: JSON.stringify({ email: email.trim() })
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || "Failed to send code");
      try { localStorage.setItem("thc-pending-signup", JSON.stringify({ email: email.trim(), name, phone })); } catch {}
      setStep("otp");
    } catch(e) { setErr(e.message); }
    finally { setSending(false); }
  };

  const handleVerifyAndCreate = async () => {
    if (!otp || otp.length !== 6) { setErr("Enter the 6-digit code."); return; }
    setSending(true); setErr("");
    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/verify-otp`, {
        method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${SUPABASE_ANON_KEY}`},
        body: JSON.stringify({ email: email.trim(), otp })
      });
      const otpData = await res.json();
      if (!otpData.verified) throw new Error(otpData.error || "Invalid code");
      // Create user server-side with hashed password
      const { data, error } = await supabase.functions.invoke("auth-login", { body: { action:"signup", email: email.trim(), password: pass, name, phone } });
      if (error || data?.error) throw new Error(data?.error || "Signup failed");
      try { localStorage.removeItem("thc-pending-signup"); } catch {}
      // Establish Supabase Auth JWT session for new user
      supabase.auth.signInWithPassword({ email: email.trim(), password: pass }).catch(() => {});
      onUserCreated?.(data.user);
      // Send welcome email with capabilities
      try {
        const role = data.user.role || "buyer";
        await supabase.functions.invoke("send-email", { body: {
          to: data.user.email,
          subject: `Welcome to THC.watch — here's what you can do`,
          html: capabilitiesHtml(role, data.user.name?.split(" ")[0]||"there"),
        }});
      } catch {}
      onLogin(data.user, true);
    } catch(e) { setErr(e.message); }
    finally { setSending(false); }
  };

  const handleForgotSendOtp = async () => {
    if (!email) { setErr("Enter your email address."); return; }
    setSending(true); setErr("");
    try {
      const { data: chk } = await supabase.functions.invoke("auth-login", { body: { action:"check_email", email: email.trim() } });
      if (!chk?.exists) { setErr("No account found with that email."); setSending(false); return; }
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/send-otp`, {
        method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${SUPABASE_ANON_KEY}`},
        body: JSON.stringify({ email: email.trim() })
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || "Failed to send code");
      setStep("otp");
    } catch(e) { setErr(e.message); }
    finally { setSending(false); }
  };

  const handleForgotVerifyOtp = async () => {
    if (!otp || otp.length !== 6) { setErr("Enter the 6-digit code."); return; }
    setSending(true); setErr("");
    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/verify-otp`, {
        method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${SUPABASE_ANON_KEY}`},
        body: JSON.stringify({ email: email.trim(), otp })
      });
      const data = await res.json();
      if (!data.verified) throw new Error(data.error || "Invalid code");
      setStep("new_password");
    } catch(e) { setErr(e.message); }
    finally { setSending(false); }
  };

  const handleForgotReset = async () => {
    if (!pass || pass.length < 6) { setErr("Password must be at least 6 characters."); return; }
    setSending(true); setErr("");
    try {
      const { data, error } = await supabase.functions.invoke("auth-login", { body: { action:"reset_password", email: email.trim(), password: pass } });
      if (error || data?.error) throw new Error(data?.error || "Reset failed");
      setResetDone(true);
    } catch(e) { setErr(e.message); }
    finally { setSending(false); }
  };

  return (
    <AuthModalInner mode={mode} setMode={setMode} step={step} setStep={setStep}
      email={email} setEmail={setEmail} pass={pass} setPass={setPass}
      name={name} setName={setName} phone={phone} setPhone={setPhone}
      otp={otp} setOtp={setOtp} sending={sending} resetDone={resetDone}
      err={err} setErr={setErr}
      handleLogin={handleLogin} handleSendOtp={handleSendOtp}
      handleVerifyAndCreate={handleVerifyAndCreate}
      handleForgotSendOtp={handleForgotSendOtp}
      handleForgotVerifyOtp={handleForgotVerifyOtp}
      handleForgotReset={handleForgotReset}
      onClose={onClose}/>
  );
}

function AuthModalInner({ mode, setMode, step, setStep, email, setEmail, pass, setPass, name, setName, phone, setPhone, otp, setOtp, sending, err, setErr, resetDone, handleLogin, handleSendOtp, handleVerifyAndCreate, handleForgotSendOtp, handleForgotVerifyOtp, handleForgotReset, onClose }) {

  const heading = mode === "forgot"
    ? (resetDone ? "Password reset" : step === "otp" ? "Check your email" : step === "new_password" ? "New password" : "Reset password")
    : mode === "login" ? "Sign in"
    : step === "otp" ? "Check your email" : "Create account";

  return (
    <div className="overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="modal fade-in" style={{maxWidth:400}}>
        <button onClick={onClose} style={{position:"absolute",top:16,right:18,background:"none",fontSize:20,color:"var(--ink3)",lineHeight:1}}>×</button>

        <div style={{textAlign:"center",marginBottom:28}}>
          <Logo size="sm"/>
        </div>

        <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,marginBottom:20,textAlign:"center"}}>
          {heading}
        </div>

        {mode==="signup" && step==="otp" && (
          <>
            <p style={{fontSize:13,color:"#4A423A",textAlign:"center",marginBottom:20,lineHeight:1.6}}>
              We sent a 6-digit code to <strong>{email}</strong>
            </p>
            <div style={{marginBottom:20}}>
              <label className="fl">Verification code *</label>
              <input value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g,"").slice(0,6))} type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6}
                placeholder="123456" style={{fontFamily:"'DM Mono',monospace",fontSize:22,letterSpacing:"0.3em",textAlign:"center"}}
                autoFocus maxLength={6}/>
            </div>
            {err && <div style={{color:"var(--red)",fontSize:12,marginBottom:14,textAlign:"center"}}>{err}</div>}
            <button className="btn-ink" style={{width:"100%",padding:13,fontSize:14,justifyContent:"center",opacity:sending?.6:1}}
              onClick={()=>handleVerifyAndCreate()} disabled={sending}>
              {sending ? "Verifying…" : "Verify & create account →"}
            </button>
            <div style={{textAlign:"center",marginTop:14,fontSize:12,color:"var(--ink3)"}}>
              Wrong email? <button onClick={()=>{setStep("details");setOtp("");setErr("");try{localStorage.removeItem("thc-pending-signup");}catch{}}} style={{background:"none",color:"var(--gold)",fontSize:12,textDecoration:"underline"}}>Go back</button>
              &nbsp;·&nbsp;
              <button onClick={()=>handleSendOtp()} style={{background:"none",color:"var(--gold)",fontSize:12,textDecoration:"underline"}}>Resend code</button>
            </div>
          </>
        )}

        {mode==="signup" && step==="details" && (
          <>
            <button style={{width:"100%",padding:"11px 16px",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"center",gap:10,borderRadius:10,border:"1.5px solid var(--bd)",background:"#fff",fontSize:13,fontWeight:500,color:"#3C4043",cursor:"pointer"}}
              onClick={()=>{ supabase.auth.signInWithOAuth({provider:"google",options:{redirectTo:window.location.origin}}); }}>
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Sign up with Google
            </button>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
              <div style={{flex:1,height:1,background:"var(--bd)"}}/>
              <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)"}}>OR</span>
              <div style={{flex:1,height:1,background:"var(--bd)"}}/>
            </div>
            <div style={{marginBottom:12}}><label className="fl">Full Name *</label><input value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" type="text" autoComplete="name" autoCapitalize="words" required/></div>
            <div style={{marginBottom:12}}><label className="fl">Email *</label><input type="email" value={email} onChange={e=>{setEmail(e.target.value);setErr("");}} placeholder="your@email.com" autoComplete="email" inputMode="email" autoCapitalize="none" spellCheck={false} required/></div>
            <div style={{marginBottom:20}}><label className="fl">Password *</label><input type="password" value={pass} onChange={e=>{setPass(e.target.value);setErr("");}} placeholder="At least 6 characters" autoComplete="new-password" minLength={6} required/></div>
            {err && <div style={{color:"var(--red)",fontSize:12,marginBottom:14,textAlign:"center"}}>{err}</div>}
            <button className="btn-ink" style={{width:"100%",padding:13,fontSize:14,justifyContent:"center",opacity:sending?.6:1}}
              onClick={()=>handleSendOtp()} disabled={sending}>
              {sending ? "Sending code…" : "Send verification code →"}
            </button>
            <div style={{textAlign:"center",marginTop:16,fontSize:13,color:"var(--ink3)"}}>
              Have an account? <button onClick={()=>{setMode("login");setErr("");setStep("details");}} style={{background:"none",color:"var(--gold)",fontWeight:500,fontSize:13,textDecoration:"underline"}}>Sign in</button>
            </div>
          </>
        )}

        {mode==="login" && (
          <>
            {/* Google sign-in — requires Google provider enabled in Supabase Auth dashboard */}
            <button style={{width:"100%",padding:"11px 16px",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"center",gap:10,borderRadius:10,border:"1.5px solid var(--bd)",background:"#fff",fontSize:13,fontWeight:500,color:"#3C4043",cursor:"pointer"}}
              onClick={()=>{ supabase.auth.signInWithOAuth({provider:"google",options:{redirectTo:window.location.origin}}); }}>
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </button>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
              <div style={{flex:1,height:1,background:"var(--bd)"}}/>
              <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)"}}>OR</span>
              <div style={{flex:1,height:1,background:"var(--bd)"}}/>
            </div>
            <div style={{marginBottom:12}}><label className="fl">Email *</label><input type="email" value={email} onChange={e=>{setEmail(e.target.value);setErr("");}} placeholder="your@email.com" autoComplete="username email" inputMode="email" autoCapitalize="none" spellCheck={false} required/></div>
            <div style={{marginBottom:4}}><label className="fl">Password *</label><input type="password" value={pass} onChange={e=>{setPass(e.target.value);setErr("");}} onKeyDown={e=>{if(e.key==="Enter")handleLogin();}} placeholder="••••••••" autoComplete="current-password" required/></div>
            <div style={{textAlign:"right",marginBottom:18}}>
              <button onClick={()=>{setMode("forgot");setStep("email");setErr("");setOtp("");setPass("");}} style={{background:"none",color:"var(--ink3)",fontSize:12,textDecoration:"underline"}}>Forgot password?</button>
            </div>
            {err && <div style={{color:"var(--red)",fontSize:12,marginBottom:14,textAlign:"center"}}>{err}</div>}
            <button className="btn-ink" style={{width:"100%",padding:13,fontSize:14,justifyContent:"center"}}
              onClick={()=>handleLogin()}>
              Sign in →
            </button>
            <div style={{textAlign:"center",marginTop:16,fontSize:13,color:"var(--ink3)"}}>
              No account? <button onClick={()=>{setMode("signup");setErr("");setStep("details");}} style={{background:"none",color:"var(--gold)",fontWeight:500,fontSize:13,textDecoration:"underline"}}>Sign up</button>
            </div>
          </>
        )}

        {/* ── Forgot password screens ─────────────────────────────────────── */}
        {mode==="forgot" && resetDone && (
          <>
            <div style={{textAlign:"center",marginBottom:24}}>
              <div style={{fontSize:36,marginBottom:12}}>✓</div>
              <p style={{fontSize:14,color:"var(--ink2)",lineHeight:1.7}}>Your password has been reset. You can now sign in with your new password.</p>
            </div>
            <button className="btn-ink" style={{width:"100%",padding:13,fontSize:14,justifyContent:"center"}}
              onClick={()=>{setMode("login");setStep("details");setErr("");}}>
              Sign in →
            </button>
          </>
        )}

        {mode==="forgot" && !resetDone && step==="email" && (
          <>
            <p style={{fontSize:13,color:"var(--ink3)",textAlign:"center",marginBottom:20,lineHeight:1.6}}>Enter the email address on your account and we'll send a reset code.</p>
            <div style={{marginBottom:20}}>
              <label className="fl">Email *</label>
              <input type="email" value={email} onChange={e=>{setEmail(e.target.value);setErr("");}} placeholder="your@email.com" autoComplete="email" inputMode="email" autoCapitalize="none" spellCheck={false} autoFocus/>
            </div>
            {err && <div style={{color:"var(--red)",fontSize:12,marginBottom:14,textAlign:"center"}}>{err}</div>}
            <button className="btn-ink" style={{width:"100%",padding:13,fontSize:14,justifyContent:"center",opacity:sending?.6:1}}
              onClick={handleForgotSendOtp} disabled={sending}>
              {sending ? "Sending…" : "Send reset code →"}
            </button>
            <div style={{textAlign:"center",marginTop:16,fontSize:13,color:"var(--ink3)"}}>
              <button onClick={()=>{setMode("login");setErr("");}} style={{background:"none",color:"var(--gold)",fontSize:13,textDecoration:"underline"}}>Back to sign in</button>
            </div>
          </>
        )}

        {mode==="forgot" && !resetDone && step==="otp" && (
          <>
            <p style={{fontSize:13,color:"var(--ink3)",textAlign:"center",marginBottom:20,lineHeight:1.6}}>
              We sent a 6-digit code to <strong>{email}</strong>
            </p>
            <div style={{marginBottom:20}}>
              <label className="fl">Verification code *</label>
              <input value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g,"").slice(0,6))} type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                placeholder="123456" style={{fontFamily:"'DM Mono',monospace",fontSize:22,letterSpacing:"0.3em",textAlign:"center"}} autoFocus/>
            </div>
            {err && <div style={{color:"var(--red)",fontSize:12,marginBottom:14,textAlign:"center"}}>{err}</div>}
            <button className="btn-ink" style={{width:"100%",padding:13,fontSize:14,justifyContent:"center",opacity:sending?.6:1}}
              onClick={handleForgotVerifyOtp} disabled={sending}>
              {sending ? "Verifying…" : "Verify code →"}
            </button>
            <div style={{textAlign:"center",marginTop:14,fontSize:12,color:"var(--ink3)"}}>
              Wrong email? <button onClick={()=>{setStep("email");setOtp("");setErr("");}} style={{background:"none",color:"var(--gold)",fontSize:12,textDecoration:"underline"}}>Go back</button>
              &nbsp;·&nbsp;
              <button onClick={handleForgotSendOtp} style={{background:"none",color:"var(--gold)",fontSize:12,textDecoration:"underline"}}>Resend code</button>
            </div>
          </>
        )}

        {mode==="forgot" && !resetDone && step==="new_password" && (
          <>
            <p style={{fontSize:13,color:"var(--ink3)",textAlign:"center",marginBottom:20,lineHeight:1.6}}>Choose a new password for <strong>{email}</strong></p>
            <div style={{marginBottom:20}}>
              <label className="fl">New password *</label>
              <input type="password" value={pass} onChange={e=>{setPass(e.target.value);setErr("");}} onKeyDown={e=>{if(e.key==="Enter")handleForgotReset();}} placeholder="At least 6 characters" autoComplete="new-password" autoFocus/>
            </div>
            {err && <div style={{color:"var(--red)",fontSize:12,marginBottom:14,textAlign:"center"}}>{err}</div>}
            <button className="btn-ink" style={{width:"100%",padding:13,fontSize:14,justifyContent:"center",opacity:sending?.6:1}}
              onClick={handleForgotReset} disabled={sending}>
              {sending ? "Resetting…" : "Reset password →"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Global users context (simple) ────────────────────────────────────────────
